import { Helmet } from '../components/Helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Search, Lightbulb, Rocket, Bot } from 'lucide-react';
import OfferCard from '../components/accompaniment/OffersCard';
import MethodPhases from '../components/accompaniment/MethodPhases';
import TargetAudience from '../components/accompaniment/TargetAudience';
import AccompanimentFAQ from '../components/accompaniment/AccompanimentFAQ';

const AccompagnementPage = () => {
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Accompagnement IA en entreprise',
    provider: {
      '@type': 'Organization',
      name: 'Upnov IA',
      url: 'https://www.upnov.com'
    },
    areaServed: 'France',
    description: 'Audit IA, ateliers cas d\'usage, lancement de projet IA en 30 jours, agents IA sur mesure pour PME et ETI.'
  };

  return (
    <>
      <Helmet>
        <title>Accompagnement IA en entreprise | Audit & Agents IA</title>
        <meta
          name="description"
          content="Upnov accompagne les PME et ETI de leur premier projet IA à sa mise en production. Audit, ateliers cas d'usage, lancement IA en 30 jours, agent IA sur mesure."
        />
        <meta name="keywords" content="accompagnement IA entreprise, agent IA PME, automatisation IA, lancement projet IA, audit IA" />
        <link rel="canonical" href="https://www.upnov.com/accompagnement-ia-entreprise" />
        <meta property="og:title" content="Accompagnement IA en entreprise | Audit & Agents IA" />
        <meta property="og:description" content="Upnov accompagne les PME et ETI de leur premier projet IA à sa mise en production. Audit, ateliers cas d'usage, lancement IA en 30 jours, agent IA sur mesure." />
        <meta property="og:url" content="https://www.upnov.com/accompagnement-ia-entreprise" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/blogimg/team-building-ia.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/5 to-accent/5 section-padding-top py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-6"
            >
              <span className="bg-primary/10 text-primary px-4 py-1 rounded-full text-sm font-semibold">
                Services IA Upnov
              </span>
            </motion.div>
            <h1 className="heading-xl mb-6 text-gray-900">
  Accélérez vos projets IA en entreprise
</h1>

<div className="text-xl text-gray-600 mb-8 space-y-3">
  <p>
    Votre premier projet IA concret en quelques jours ou semaines.
  </p>

  <p>
    Automatisation, agents IA, emails, devis, support, compta…
  </p>

  <p>
    Prenez de l’avance dès maintenant.
  </p>
</div>

<div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href="/contact/" className="btn-primary">
                Réserver un audit gratuit 30 min
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <a href="#offres" className="btn-secondary">
                Voir nos offres
              </a>
            </div>

            {/* Proof points */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 bg-white rounded-xl p-8 shadow-sm">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">1 300+</div>
                <div className="text-sm text-gray-600">pros formés</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">200+</div>
                <div className="text-sm text-gray-600">entreprises</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">98%</div>
                <div className="text-sm text-gray-600">satisfaction</div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg mb-6">Former ne suffit pas. Il faut livrer.</h2>
            <p className="text-lg text-gray-600">
              80 % des acquis de formation IA disparaissent en 7 jours si aucun projet concret n'est lancé dans la foulée. Notre méthode comble ce trou : nous ancrons la formation de vos équipes dans un projet livré en 30 à 90 jours — agent IA, automatisation de processus, ou les deux.
            </p>
          </div>
        </div>
      </section>

      {/* Offers Section */}
      <section id="offres" className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="heading-lg mb-4">Quatre offres, un parcours</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choisissez l'entrée qui correspond à votre maturité
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <OfferCard
              icon={<Search className="h-6 w-6" />}
              title="Audit IA"
              tagline="Cadrer avant d'agir"
              description="Diagnostic de maturité, cartographie des données, identification des cas d'usage prioritaires. Livrable : feuille de route IA personnalisée."
              duration="1 à 3 jours"
              cta="En savoir plus"
              ctaLink="/contact?utm_source=hub&utm_content=audit-ia"
            />

            <OfferCard
              icon={<Lightbulb className="h-6 w-6" />}
              title="Atelier cas d'usage IA"
              tagline="Identifier vos opportunités"
              description="Atelier collectif d'une journée pour faire émerger les cas d'usage IA propres à vos métiers, avec démos live et chiffrage business."
              duration="1 jour"
              cta="En savoir plus"
              ctaLink="/contact?utm_source=hub&utm_content=atelier-cas-usage"
            />

            <OfferCard
              icon={<Rocket className="h-6 w-6" />}
              title="Lancement IA"
              tagline="Votre premier projet livré"
              description="Forfait tout inclus : audit, formation des équipes, conception et livraison d'un agent IA ou d'une automatisation, mesure du ROI."
              duration="À partir de 30 jours"
              cta="En savoir plus"
              ctaLink="/contact?utm_source=hub&utm_content=lancement-ia"
              featured
            />

            <OfferCard
              icon={<Bot className="h-6 w-6" />}
              title="Agent IA sur mesure"
              tagline="Industrialiser un cas d'usage"
              description="Conception et déploiement d'un agent IA spécifique à votre métier (commercial, support, RH, juridique…) avec intégration à vos outils."
              duration="2 à 6 semaines"
              cta="En savoir plus"
              ctaLink="/contact?utm_source=hub&utm_content=agent-ia"
            />
          </div>
        </div>
      </section>

      {/* Method Phases */}
      <MethodPhases />

      {/* Target Audience */}
      <TargetAudience />

      {/* Testimonials */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="heading-lg text-center mb-16">Témoignages clients</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "Grâce à Upnov, nous avons automatisé nos devis et mises à jour CRM. Nous sommes passés de plusieurs jours à quelques heures.",
                author: "Thomas",
                position: "Directeur Commercial, PME industrielle"
              },
              {
                quote: "Les agents IA mis en place par Upnov nous font gagner un temps énorme sur le support client et les tâches administratives.",
                author: "Sophie",
                position: "Responsable Opérations, e-commerce"
              },
              {
                quote: "Upnov a connecté nos outils internes avec l'IA pour automatiser nos process métier. Les équipes trouvent l'information beaucoup plus vite.",
                author: "Marc",
                position: "DSI, société de Conseil en investissement"
              }
            ].map((testimonial, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow"
              >
                <blockquote className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.position}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Client References */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <p className="text-sm text-gray-600 mb-8 text-center">
            <span className="font-semibold">Ils nous font confiance :</span>
          </p>

          <div className="grid grid-cols-3 md:grid-cols-6 gap-6 md:gap-8 items-center justify-center">
            {[
              'edf', 'ratp', 'orano', 'loreal', 'thales', 'vinci', 'eiffage',
              'CaissedesDepots', 'laposte', 'safran', 'yvesRocher', 'sncf'
            ].map((logo) => (
              <motion.img
                key={logo}
                src={`/logo/logo-${logo}.webp`}
                alt={`${logo} logo`}
                width="400"
                height="160"
                className="h-16 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <AccompanimentFAQ />

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary to-accent text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg mb-6">Prêt à lancer votre premier projet IA ?</h2>
            <p className="text-xl text-white/90 mb-8">
              30 minutes pour cadrer votre démarche, identifier vos opportunités et vérifier si nous sommes le bon partenaire.
            </p>
            <a href="/contact/" className="btn-primary bg-white text-primary hover:bg-gray-50">
              Réserver un audit gratuit 30 min
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AccompagnementPage;
